/**
 * src/hooks/useBoloAlert.ts — v0.4.9.2.3c3 PWA
 *
 * Subscribes to active BOLOs for the current civil respondent / agent /
 * guard user.
 *
 * c3 additions vs c2:
 *   - 10s polling fallback (configurable) so WS-less mobile sessions
 *     still see new BOLOs without forcing the user to background/
 *     foreground the app (Bug00).
 *   - ack timeout: when no alert_id is available after N seconds (e.g.
 *     race between WS message and API fetch), the bolo is auto-removed
 *     from the local list to prevent stuck "Waiting for server
 *     confirmation…" cards (Bug05).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

interface ActiveBolo {
  id: string;
  alert_id: string | null;
  target_type: string;
  target_name: string | null;
  target_description: string | null;
  target_photo_url: string | null;
  scope: string;
  country: string;
  country_code: string | null;
  protocol: string;
  issued_at: string;
  issuing_org_name: string | null;
  alert_status: 'active' | 'acknowledged' | 'cancelled';
}

interface Options {
  pollIntervalMs?: number;
  ackTimeoutMs?: number;
}

export function useBoloAlert(opts: Options = {}) {
  const { user } = useAuth();
  const pollIntervalMs = opts.pollIntervalMs ?? 10_000;
  const ackTimeoutMs = opts.ackTimeoutMs ?? 15_000;
  const [activeBolos, setActiveBolos] = useState<ActiveBolo[]>([]);
  const [loading, setLoading] = useState(false);
  // Track when each alert_id was first seen without one — used to
  // auto-clear stuck cards after `ackTimeoutMs`.
  const stuckSinceRef = useRef<Map<string, number>>(new Map());

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const fn = (api as any).getActiveBolos || (api as any).listActiveBolos;
      const list = fn ? await fn() : [];
      const fresh: ActiveBolo[] = Array.isArray(list) ? list : [];

      // Reset the stuck-since timer for items that now have alert_id
      const next = new Map(stuckSinceRef.current);
      fresh.forEach((b) => {
        if (b.alert_id) next.delete(b.id);
      });
      stuckSinceRef.current = next;

      setActiveBolos(fresh);
    } catch (err) {
      console.error('[useBoloAlert] refresh failed:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  // Polling fallback (every pollIntervalMs). Fixes mobile Bug00.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      refresh();
    }, pollIntervalMs);
    return () => clearInterval(interval);
  }, [user, refresh, pollIntervalMs]);

  // Stuck-card sweeper — once ackTimeoutMs passes for a card with no
  // alert_id, drop it from the list. Prevents infinite "Waiting for
  // server confirmation…" cards.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const stuck = stuckSinceRef.current;
      setActiveBolos((prev) => {
        let changed = false;
        const next = prev.filter((b) => {
          if (b.alert_id) return true; // has alert_id — keep
          if (!stuck.has(b.id)) {
            stuck.set(b.id, now);
            return true; // first time we see it without alert_id — keep, mark stuck
          }
          if (now - (stuck.get(b.id) || 0) > ackTimeoutMs) {
            console.warn(`[useBoloAlert] dropping stuck BOLO ${b.id} (no alert_id after ${ackTimeoutMs}ms)`);
            stuck.delete(b.id);
            changed = true;
            return false;
          }
          return true;
        });
        return changed ? next : prev;
      });
    }, 2_000); // sweep every 2s
    return () => clearInterval(interval);
  }, [ackTimeoutMs]);

  // WS subscription — OPTIMISTIC + refresh, fully defensive
  //
  // c4 hotfix: every step is wrapped in a try/catch + null-guard so
  // a malformed WS message can't take down the React tree. The APK
  // black-screen on login was traced back to this handler throwing
  // on unexpected msg shapes from older WS dispatcher versions.
  useEffect(() => {
    let cancelled = false;
    const onBoloMessage = (msg: any) => {
      try {
        if (cancelled) return;
        if (!msg || typeof msg !== 'object') return;
        if (msg.type !== 'alert') return;
        const wsBolo = msg.alert;
        if (!wsBolo || typeof wsBolo !== 'object') return;
        const category = wsBolo.category;
        if (category !== 'bolo' && category !== 'bolo_cancelled') return;

        // Optimistic insert ONLY if we have enough fields. If
        // boloId or targetType is missing, skip the optimistic and
        // just refresh — the API response will populate the card.
        if (category === 'bolo' && wsBolo.boloId && wsBolo.targetType) {
          setActiveBolos((prev) => {
            try {
              const idx = prev.findIndex((p) => p && p.id === wsBolo.boloId);
              if (idx >= 0) return prev;
              const optimistic: ActiveBolo = {
                id: String(wsBolo.boloId),
                alert_id: null,
                target_type: String(wsBolo.targetType),
                target_name: wsBolo.targetName ?? null,
                target_description: wsBolo.targetDescription ?? null,
                target_photo_url: wsBolo.targetPhotoUrl ?? null,
                scope: wsBolo.scope ?? 'network_wide',
                country: wsBolo.country ?? '',
                country_code: wsBolo.countryCode ?? null,
                protocol: wsBolo.protocol ?? null,
                issued_at: wsBolo.timestamp || new Date().toISOString(),
                issuing_org_name: null,
                alert_status: 'active',
              };
              return [optimistic, ...prev];
            } catch {
              return prev;
            }
          });
        }
        // Trigger API refresh to fill in alert_id and any missing data.
        refresh();
      } catch (err) {
        // Last-resort safety net — never let the WS handler kill the page.
        console.error('[useBoloAlert] WS handler error (caught):', err);
      }
    };

    // v0.4.9.2.3: PWA has no WebSocket client. Real-time alert delivery
    // is:
    //   - APK path: native AlertWebSocket (Kotlin) → Android system notification
    //   - PWA path: 10s polling fallback (see pollIntervalMs effect above)
    // If a PWA-side WS client is added later, dispatch 'iSentinel360:alert'
    // and re-add a listener here.
    // the lines of code bellow seems confusing, it is not it is provision
    // for later, if I decide not to add it and use 10 sec delay instead this will
    // be removed...
    window.addEventListener('bolo-alert', onBoloMessage as EventListener);
    const anyWin = window as any;
    if (anyWin.__alertsWs?.addListener) {
      anyWin.__alertsWs.addListener(onBoloMessage);
    }
    return () => {
      cancelled = true;
      window.removeEventListener('bolo-alert', onBoloMessage as EventListener);
      anyWin.__alertsWs?.removeListener?.(onBoloMessage);
    };
  }, [refresh]);

  const dismiss = useCallback(async (alertId: string | null) => {
    if (!alertId || alertId === 'null') {
      console.error('[useBoloAlert] dismiss: alert_id is null');
      throw new Error('alert_id unavailable');
    }
    try {
      const fn = (api as any).acknowledgeBolo;
      if (!fn) throw new Error('api.acknowledgeBolo not implemented');
      await fn(alertId, { via: 'web' });
      setActiveBolos((prev) => prev.filter((b) => b.alert_id !== alertId));
    } catch (err) {
      console.error('[useBoloAlert] ack failed:', err);
      throw err;
    }
  }, []);

  const unreadCount = activeBolos.filter((b) => b.alert_status === 'active').length;

  return { activeBolos, unreadCount, loading, refresh, dismiss };
}
